/**
 * Değerlendirme soruları (mobil utils/library/questionRules.ts ve
 * saveAsPackage.ts ile aynı kurallar). Müzakere sihirbazı, soru düzenleme ve
 * kütüphane paket düzenleyicisi aynı kuralları kullanır.
 * Soru: { qid, type, text, options?, correctIndex?, correctBool?, sourceQid? }
 */

/** Sunucudaki sınırlarla aynı (questionRules.js ve paket modeli). */
export const LIMITS = {
  titleMax: 80,
  descriptionMax: 1000,
  questionsMax: 30,
  questionTextMax: 300,
  optionsMin: 2,
  optionsMax: 6,
  optionMax: 120,
};

export const QUESTION_TYPES = ["coktan", "evethayir", "dogruyanlis", "acik"];

/** Tip adlarının çeviri anahtarları. */
export const TYPE_LABEL = {
  coktan: "mzq_type_coktan",
  evethayir: "mzq_type_evethayir",
  dogruyanlis: "mzq_type_dogruyanlis",
  acik: "mzq_type_acik",
};

const ISSUE_TEXT = {
  text: "mzq_issue_text",
  textLength: "mzq_issue_text_length",
  optionsCount: "mzq_issue_options_count",
  optionEmpty: "mzq_issue_option_empty",
  optionLength: "mzq_issue_option_length",
  correct: "mzq_issue_correct",
  tooMany: "mzq_issue_too_many",
};

/** Eksiğin çeviri anahtarı. */
export const issueText = (code) => ISSUE_TEXT[code] || ISSUE_TEXT.text;

const HEX24 = /^[0-9a-f]{24}$/i;

/** Sunucunun verdiği kalıcı kimlik mi (yerel geçici kimlik değil). */
export const isServerId = (id) => typeof id === "string" && HEX24.test(id);

let localSeq = 0;
export const newLocalQid = () => `local-${Date.now().toString(36)}-${(localSeq += 1)}`;

const hasBool = (type) => type === "evethayir" || type === "dogruyanlis";

/** Sorunun ilk eksiği; yoksa null. */
export function questionIssue(q) {
  const text = String(q?.text || "").trim();
  if (!text) return "text";
  if (text.length > LIMITS.questionTextMax) return "textLength";
  if (q.type === "coktan") {
    const options = q.options || [];
    if (options.length < LIMITS.optionsMin || options.length > LIMITS.optionsMax) return "optionsCount";
    if (options.some((o) => !String(o || "").trim())) return "optionEmpty";
    if (options.some((o) => String(o).trim().length > LIMITS.optionMax)) return "optionLength";
    const ci = q.correctIndex;
    if (typeof ci !== "number" || !Number.isInteger(ci) || ci < 0 || ci >= options.length) return "correct";
  }
  if (hasBool(q.type) && typeof q.correctBool !== "boolean") return "correct";
  return null;
}

/** Listedeki ilk hatalı soru (sırasıyla) ya da fazla soru; yoksa null. */
export function questionsIssue(list) {
  const items = list || [];
  if (items.length > LIMITS.questionsMax) return { index: LIMITS.questionsMax, code: "tooMany" };
  for (let i = 0; i < items.length; i += 1) {
    const code = questionIssue(items[i]);
    if (code) return { index: i, code };
  }
  return null;
}

/** Tip değişince metin kalır; tipe ait olmayan alanlar atılır. */
export function withType(q, type) {
  const base = { qid: q.qid, type, text: q.text || "" };
  if (q.sourceQid) base.sourceQid = q.sourceQid;
  if (type === "coktan") {
    base.options = q.type === "coktan" && q.options?.length ? [...q.options] : ["", ""];
    if (q.type === "coktan" && typeof q.correctIndex === "number") base.correctIndex = q.correctIndex;
  }
  if (hasBool(type) && hasBool(q.type) && typeof q.correctBool === "boolean") base.correctBool = q.correctBool;
  return base;
}

export function blankQuestion(type = "coktan") {
  return withType({ qid: newLocalQid(), type, text: "" }, type);
}

/** Şık silinince doğru şık kayar; silinen doğru şıksa seçim kalkar. */
export function removeOption(q, index) {
  const options = (q.options || []).filter((_, j) => j !== index);
  const ci = q.correctIndex;
  const next = { ...q, options };
  if (typeof ci !== "number" || ci === index) delete next.correctIndex;
  else next.correctIndex = ci > index ? ci - 1 : ci;
  return next;
}

/** Kaydederken: metin ve şıklar kırpılır. */
export function trimQuestion(q) {
  const out = { ...q, text: String(q.text || "").trim() };
  if (q.options) out.options = q.options.map((o) => String(o || "").trim());
  return out;
}

/** Sunucuya giden biçim: kırpılmış, yalnız tipe ait alanlar, yerel kimlik yok. */
export function toPayload(q) {
  const out = isServerId(q.qid) ? { qid: q.qid, type: q.type } : { type: q.type };
  out.text = String(q.text || "").trim();
  if (q.type === "coktan") {
    out.options = (q.options || []).map((o) => String(o || "").trim());
    if (typeof q.correctIndex === "number") out.correctIndex = q.correctIndex;
  }
  if (hasBool(q.type) && typeof q.correctBool === "boolean") out.correctBool = q.correctBool;
  if (isServerId(q.sourceQid)) out.sourceQid = q.sourceQid;
  return out;
}

/** Soru listesinin kopyası (şıklar da kopyalanır). */
export const cloneQuestions = (list) =>
  (list || []).map((q) => (q.options ? { ...q, options: [...q.options] } : { ...q }));

/** Doğru cevabın okunur hali (yönetici görünümü); doğru cevap yoksa boş. */
export function correctAnswerLabel(q, t) {
  if (q.type === "acik") return "";
  if (q.type === "coktan") {
    return typeof q.correctIndex === "number" ? q.options?.[q.correctIndex] ?? "" : "";
  }
  if (typeof q.correctBool !== "boolean") return "";
  if (q.type === "evethayir") return t(q.correctBool ? "assess_yes" : "assess_no");
  return t(q.correctBool ? "assess_true" : "assess_false");
}

/* ---------- Pakete kaydet ---------- */

export const PACKAGE_TITLE_MAX = LIMITS.titleMax;

const sameQuestion = (a, b) =>
  a.qid === b.qid &&
  a.type === b.type &&
  a.text === b.text &&
  a.correctIndex === b.correctIndex &&
  a.correctBool === b.correctBool &&
  JSON.stringify(a.options ?? null) === JSON.stringify(b.options ?? null);

/**
 * "Paket olarak kaydet" yalnız kütüphaneyi kullanabilene, en az bir soru
 * varken ve sorular seçili paketin değişmemiş kopyası değilken sunulur.
 * Paket seçiliyse ama karşılaştıracak kopya yoksa sunulmaz.
 */
export function shouldOfferSaveAsPackage({ canUse, questions, packageId, packageQuestions }) {
  if (!canUse || !questions?.length) return false;
  if (!packageId) return true;
  if (!packageQuestions) return false;
  if (packageQuestions.length !== questions.length) return true;
  return questions.some((q, i) => !sameQuestion(q, packageQuestions[i]));
}

/** Kilitli paketin neden kilitli olduğu (çeviri anahtarı). */
export function lockReasonKey(pkg) {
  if (pkg?.lockReason === "A") return "mzq_lock_reason_a";
  if (pkg?.lockReason === "B") return "mzq_lock_reason_b";
  return "mzq_lock_reason";
}
